/**
 * Propriétés pour les envois de formulaire
 * Cela va être enregistré dans le BPMN sous la forme :
 * <bpmn:sendTask id="a" name="mail" 
 *  aktor:send-form="...">
 *     <aktor:form-json>{...}</aktor:form-json>
 * </bpmn:sendTask>
 * 
 * aktor:send-form :
 * - (vide) pour ne pas envoyer de formulaire
 * - 'self' : dans ce cas le formulaire est défini dans la balise <aktor:form-json>
 * - 'Activity_0tfdakq' : pour toute autre valeur, il s'agit de l'id d'un autre élément SendTask.
 *   Il faudra donc lire le <aktor:form-json> de l'élément SendTask en question.
 * 
 * aktor:form-json :
 * Formulaire en json, par exemple :
 * {
 *   "name": "A",
 *   "languages": [
 *     "fr"
 *   ],
 *   "fields": [
 *     {
 *       "name": "field1",
 *       "type": "text",
 *       "label": {
 *         "fr": "A"
 *       },
 *       "helperText": {}
 *     }
 *   ]
 * }
 */
import { html } from 'htm/preact';
import { SelectEntry, isSelectEntryEdited } from '@bpmn-io/properties-panel';
import { useService } from 'bpmn-js-properties-panel';
import EventBus from '@services/EventBus';

// Écouter l'événement de mise à jour du JSON
EventBus.on('updateFormJson', (data) => {
  const modeling = data.modeling;
  modeling.updateProperties(data.element, {
    'form-json': data.json
  });
});

function FormProps(element) {
  let fields = [{
    id: 'choose-form',
    element,
    component: ChooseFormField,
    isEdited: isSelectEntryEdited
  }, {
    id: 'action-button',
    element,
    component: ActionButton
  }];

  return fields;
}

function ChooseFormField({ element, id }) {
  const modeling = useService('modeling');
  const translate = useService('translate');
  const debounce = useService('debounceInput');
  const bpmnjs = useService('bpmnjs');

  const getValue = () => {
    return element.businessObject['send-form'] || '';
  };

  const setValue = (value) => {
    let result = {};
    result['send-form'] = value;
    
    if (value === 'self') {
      setTimeout(() => {
        EventBus.emit('openFormDialog', { element, modeling, bpmnjs });
      }, 0);
    } else {
      result['form-json'] = undefined;
    }
    
    return modeling.updateProperties(element, result);
  };

  const getOptions = () => {
    const elementRegistry = bpmnjs.get('elementRegistry');
    const sendTasks = elementRegistry.filter(element =>
      element.type === 'bpmn:SendTask'
    );
    //console.log(sendTasks[0].businessObject['form-json']);

    // Parcours des tâches d'envoi et récupération du nom du formulaire
    let hasSelf = false;
    const formNames = sendTasks
      .map(task => {
        const formJsonStr = task.businessObject['form-json'];
        if (formJsonStr) {
          const isSelf = element.id === task.id;
          if (isSelf) {
            hasSelf = true;
          }
          const formJson = JSON.parse(formJsonStr);
          if (formJson.name) {
            return {
              value: isSelf ? 'self' : task.id,
              label: formJson.name
            }
          }
        }
        return null;
      })
      .filter(value => value !== null);

    let result = [{
      value: '',
      label: translate('None')
    }];
    if (!hasSelf) {
      result.push({
        value: 'self',
        label: translate('New form')
      });
    }
    return [...result, ...formNames];
  };

  return html`<${SelectEntry}
    id=${id}
    element=${element}
    label=${translate('Send form')}
    getValue=${getValue}
    setValue=${setValue}
    getOptions=${getOptions}
    debounce=${debounce}
  />`;
}

function ActionButton({ element }) {
  const translate = useService('translate');
  const modeling = useService('modeling');
  const bpmnjs = useService('bpmnjs');
  
  const handleClick = () => {
    EventBus.emit('openFormDialog', { element, modeling, bpmnjs });
  };

  if (element.businessObject['send-form'] === 'self') {
    return html`
      <div class="bio-properties-panel-entry">
        <button 
        type="button"
        class="bpp-button" 
        onClick=${handleClick}
      >
        ${translate('Configure form')}
      </button>
    </div>
  `;
  }

  return null;
}

export default FormProps;
